import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Trash2, ArrowLeft, ShieldCheck, Pin, Search, X, ChevronUp, ChevronDown, Circle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EditorsPickPanel = () => {
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState({}); // { [itemId]: string }

  const refresh = async () => {
    try {
      const res = await api.get("/editors_pick/queue");
      setItems(res.data?.items || []);
      setActiveIndex(res.data?.active_index ?? 0);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/recipes?q=${encodeURIComponent(query)}&limit=8`)
        .then((res) => setResults(res.data))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const addToQueue = async (recipeId) => {
    try {
      await api.post("/editors_pick/queue", { recipe_id: recipeId, note: newNote.trim() || null });
      setQuery("");
      setResults([]);
      setNewNote("");
      toast.success("Added to queue");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  const saveNote = async (itemId) => {
    const note = editingNote[itemId] ?? "";
    try {
      await api.patch(`/editors_pick/queue/${itemId}`, { recipe_id: "ignored", note: note.trim() || null });
      setEditingNote((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      toast.success("Note updated");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm("Remove this pick from the queue?")) return;
    try {
      await api.delete(`/editors_pick/queue/${itemId}`);
      toast.success("Removed from queue");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const move = async (itemId, direction) => {
    try {
      await api.post(`/editors_pick/queue/${itemId}/move`, { direction });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear the entire queue? The Home page rail will disappear until you re-queue.")) return;
    try {
      await api.delete("/editors_pick");
      toast.success("Queue cleared");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <section
      className="mb-16 border border-lime-900/40 bg-lime-950/10 p-6 md:p-8"
      data-testid="editors-pick-panel"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Pin className="w-4 h-4 text-lime-500" />
          <span className="eyebrow text-lime-500">Editor&apos;s Pick — weekly auto-rotating queue</span>
        </div>
        {items.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="editors-pick-clear-all"
            className="text-[10px] uppercase tracking-[0.24em] text-orange-500 hover:text-orange-400 ink-link"
          >
            Clear queue
          </button>
        )}
      </div>

      <p className="text-sm text-stone-400 leading-relaxed mb-6">
        Queue as many recipes as you like. The Home page rotates through them, one per ISO week.
        {items.length > 0 && (
          <>
            {" "}
            Currently showing item{" "}
            <span className="text-lime-500 font-medium">#{activeIndex + 1}</span> of {items.length}.
          </>
        )}
      </p>

      {items.length > 0 && (
        <ol className="space-y-3 mb-8" data-testid="editors-pick-queue-list">
          {items.map((it, idx) => {
            const isActive = idx === activeIndex;
            const noteValue = editingNote[it.id] ?? it.note ?? "";
            const isEditing = editingNote[it.id] !== undefined;
            return (
              <li
                key={it.id}
                data-testid={`editors-pick-item-${it.id}`}
                className={`border ${isActive ? "border-lime-600 bg-lime-950/20" : "border-stone-800 bg-stone-900/40"} p-5`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center pt-1">
                    <Circle
                      className={`w-3 h-3 ${isActive ? "text-lime-500 fill-lime-500" : "text-stone-600"}`}
                    />
                    <span className={`mt-2 text-[9px] uppercase tracking-[0.24em] ${isActive ? "text-lime-500" : "text-stone-500"}`}>
                      #{idx + 1}
                    </span>
                  </div>
                  <div className="w-20 aspect-[4/5] bg-stone-900 overflow-hidden shrink-0">
                    {it.recipe?.image_status === "ready" && (
                      <img
                        src={`${BACKEND}/api/recipes/${it.recipe.id}/og_image`}
                        alt={it.recipe.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <h3 className="font-serif-display text-xl text-stone-100 leading-tight" data-testid={`editors-pick-title-${it.id}`}>
                        {it.recipe?.title || <span className="text-orange-500 italic">Recipe deleted — remove this item</span>}
                      </h3>
                      {isActive && (
                        <span className="text-[9px] uppercase tracking-[0.24em] text-lime-500">
                          Live this week
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-stone-500 mt-1">
                      {it.recipe?.region || ""} · added by {it.pinned_by || "unknown"}
                    </p>
                    {isEditing ? (
                      <div className="mt-3 space-y-2">
                        <textarea
                          value={noteValue}
                          onChange={(e) => setEditingNote((p) => ({ ...p, [it.id]: e.target.value }))}
                          maxLength={280}
                          rows={2}
                          data-testid={`editors-pick-note-${it.id}`}
                          className="w-full bg-stone-950 border border-stone-800 focus:border-lime-600 text-stone-100 px-3 py-2 outline-none text-sm resize-none"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => saveNote(it.id)}
                            data-testid={`editors-pick-save-note-${it.id}`}
                            className="text-[10px] uppercase tracking-[0.24em] bg-lime-700 hover:bg-lime-800 text-stone-50 px-3 py-1.5"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setEditingNote((p) => {
                                const n = { ...p };
                                delete n[it.id];
                                return n;
                              })
                            }
                            className="text-[10px] uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 ink-link"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      it.note && (
                        <blockquote className="mt-3 font-serif-display italic text-sm text-stone-300 border-l-2 border-lime-800/60 pl-3">
                          &ldquo;{it.note}&rdquo;
                        </blockquote>
                      )
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => move(it.id, "up")}
                      data-testid={`editors-pick-up-${it.id}`}
                      className="p-1.5 border border-stone-800 hover:border-lime-600 disabled:opacity-30 disabled:hover:border-stone-800 text-stone-400"
                      aria-label="Move up"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === items.length - 1}
                      onClick={() => move(it.id, "down")}
                      data-testid={`editors-pick-down-${it.id}`}
                      className="p-1.5 border border-stone-800 hover:border-lime-600 disabled:opacity-30 disabled:hover:border-stone-800 text-stone-400"
                      aria-label="Move down"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => setEditingNote((p) => ({ ...p, [it.id]: it.note ?? "" }))}
                        data-testid={`editors-pick-edit-${it.id}`}
                        className="p-1.5 border border-stone-800 hover:border-lime-600 text-stone-400"
                        aria-label="Edit note"
                      >
                        <span className="text-[10px] leading-none">Aa</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeItem(it.id)}
                      data-testid={`editors-pick-remove-${it.id}`}
                      className="p-1.5 border border-orange-900/50 hover:border-orange-500 text-orange-500"
                      aria-label="Remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div className="pt-6 border-t border-stone-800">
        <p className="text-[10px] uppercase tracking-[0.24em] text-stone-500 mb-3">
          Add another to the queue
        </p>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search recipe title..."
            data-testid="editors-pick-search"
            className="w-full bg-stone-950 border border-stone-800 focus:border-lime-600 text-stone-100 pl-11 pr-4 py-3 outline-none"
          />
        </div>
        {results.length > 0 && (
          <>
            <input
              type="text"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              maxLength={280}
              placeholder="Optional editor's note for the pick you choose"
              data-testid="editors-pick-new-note"
              className="mt-3 w-full bg-stone-950 border border-stone-800 focus:border-lime-600 text-stone-100 px-4 py-2 outline-none text-sm"
            />
            <ul className="mt-3 space-y-1 max-h-64 overflow-y-auto" data-testid="editors-pick-results">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => addToQueue(r.id)}
                    data-testid={`editors-pick-result-${r.id}`}
                    className="w-full text-left px-4 py-3 border border-stone-800 hover:border-lime-600 hover:bg-stone-900 flex items-center justify-between gap-4 transition-colors"
                  >
                    <span className="font-serif-display text-stone-100">{r.title}</span>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
                      {r.region || r.course}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
};

const AdminModeration = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      navigate("/");
      return;
    }
    api.get("/admin/cook_photos")
      .then((res) => setPhotos(res.data?.photos || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo? This is irreversible.")) return;
    try {
      await api.delete(`/cook_photos/${photoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 pt-16 pb-32">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link mb-12"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Home
        </Link>

        <div className="mb-16 border-b border-stone-800 pb-12">
          <div className="flex items-center gap-4 mb-6">
            <ShieldCheck className="w-4 h-4 text-orange-500" />
            <span className="eyebrow text-orange-500">Chef&apos;s Pass · Moderation</span>
          </div>
          <h1
            data-testid="admin-title"
            className="font-serif-display text-6xl md:text-7xl text-stone-100 leading-[0.9] tracking-tighter"
          >
            Cook <span className="italic text-orange-600">photographs</span>.
          </h1>
          <p className="mt-6 max-w-xl text-sm text-stone-400 leading-relaxed">
            {photos.length} user-uploaded photo{photos.length === 1 ? "" : "s"} across the library.
            Pull down anything that isn&apos;t food, isn&apos;t safe for the pass, or just isn&apos;t up to standard.
          </p>
        </div>

        <EditorsPickPanel />

        {photos.length === 0 ? (
          <div className="text-center py-24 border border-stone-800">
            <p className="font-serif-display italic text-2xl text-stone-400">
              Nothing to moderate. Quiet service today.
            </p>
          </div>
        ) : (
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="admin-photo-grid"
          >
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-photo-${p.id}`}
                className="border border-stone-800 bg-stone-900/30 overflow-hidden group"
              >
                <div className="aspect-[4/3] bg-stone-900 relative">
                  <img
                    src={`${BACKEND}/api/cook_photos/${p.id}/image`}
                    alt={p.caption || "cook photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <button
                    type="button"
                    onClick={() => deletePhoto(p.id)}
                    data-testid={`admin-photo-delete-${p.id}`}
                    className="absolute top-3 right-3 bg-orange-700 hover:bg-orange-800 text-stone-50 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-4 space-y-2">
                  {p.caption && (
                    <p className="font-serif-display italic text-sm text-stone-200 leading-snug">
                      &ldquo;{p.caption}&rdquo;
                    </p>
                  )}
                  <p className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
                    {p.user_display_name || "anonymous cook"}
                    {p.created_at && (
                      <>
                        {" · "}
                        {new Date(p.created_at).toLocaleDateString()}
                      </>
                    )}
                  </p>
                  <Link
                    to={`/recipe/${p.recipe_id}`}
                    className="block text-[10px] uppercase tracking-[0.24em] text-orange-500 hover:text-orange-400 ink-link"
                  >
                    View recipe →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminModeration;
